import { QueryFactory } from '@lmt/shared-types';
import {
  logOut,
  useClaims,
} from '@lmt/unicorn-shared/app/features/auth/operations';
import { QuerySuspenseErrorWrapper } from '@lmt/unicorn-shared/app/helpers/retry-helper';
import { usePrefetchQueries } from '@lmt/unicorn-shared/app/usePrefetchQueries';
import React, { FC, useEffect, useMemo } from 'react';
import { IdleTimerProvider } from 'react-idle-timer';
import { SignalRHandler } from '../signalr/SignalRHandler';
import { useTenantAdministratorRoleId } from '@lmt/unicorn-shared/app/features/roles/selectors';
import { useGetUserById } from '@lmt/unicorn-shared/app/features/users/selectors';

function useConfigureQueriesAccordingToPermissions() {
  const claims = useClaims();
  useEffect(() => {
    QueryFactory.HL7OutgoingSettingsQuery.setGetDefaultOptions({
      ...QueryFactory.HL7OutgoingSettingsQuery.getGetDefaultOptions(),
      enabled: claims.permissions.hl7 || claims.permissions.hl7Management,
    });
    QueryFactory.HL7SettingsQuery.setGetDefaultOptions({
      ...QueryFactory.HL7SettingsQuery.getGetDefaultOptions(),
      enabled: claims.permissions.hl7 || claims.permissions.hl7Management,
    });
  }, [claims.permissions.hl7, claims.permissions.hl7Management]);
}

export const InactivityWatcher: FC<React.PropsWithChildren> = (props) => {
  const claims = useClaims();
  const { data: settings } = QueryFactory.SecuritySettingsQuery.useGetQuery({
    throwOnError: false,
  });
  const user = useGetUserById(claims.id);
  const adminRoleId = useTenantAdministratorRoleId();

  const logoutTimeout = useMemo(() => {
    if (!settings) {
      return;
    }
    // admin auto logout only for tenant administrator
    if (adminRoleId && user?.roles.includes(adminRoleId)) {
      const value = window.localStorage.getItem('admin_logout');
      if (value === 'disabled_for_testing') {
        return;
      }
      return settings.adminLogoutTimeoutMinutes
        ? settings.adminLogoutTimeoutMinutes * 60 * 1000
        : undefined;
    }

    // We may use this code in the future
    /*if (claims.permissions.developerMode) {
      return settings.idleLogoutTimeoutMinutes
        ? settings.idleLogoutTimeoutMinutes * 60 * 1000
        : undefined;
    }
    if (
      claims.permissions.tenantSettingsChange ||
      claims.permissions.superuser
    ) {
      const value = window.localStorage.getItem('admin_logout');
      if (value === 'disabled_for_testing') {
        return;
      }
      return settings.adminLogoutTimeoutMinutes
        ? settings.adminLogoutTimeoutMinutes * 60 * 1000
        : undefined;
    }*/

    if (settings.idleLogoutTimeoutMinutes) {
      return settings.idleLogoutTimeoutMinutes * 60 * 1000;
    }
    return;
  }, [settings, claims.id, user?.roles, adminRoleId]);

  return (
    <IdleTimerProvider
      timeout={logoutTimeout || 600000000}
      stopOnIdle={true}
      onIdle={logOut}
      disabled={!logoutTimeout}
    >
      {props.children}
    </IdleTimerProvider>
  );
};

export const LoginLoader: FC<React.PropsWithChildren> = (props) => {
  useConfigureQueriesAccordingToPermissions();
  usePrefetchQueries();

  return (
    <QuerySuspenseErrorWrapper>
      <InactivityWatcher>
        <SignalRHandler />
        {props.children as React.ReactElement<any>}
      </InactivityWatcher>
    </QuerySuspenseErrorWrapper>
  );
};
